import { useEffect, useMemo } from "react";
import { useNavigate } from "react-router";
import { useAppSelector } from "../../../store/hooks";
import { authSlice } from "../../auth";
import { useGetCurrentUserQuery } from "../../users";
import { useGetResourcesQuery } from "../../resources";
import {
  useGetModelsQuery,
  useGetModelPricingsQuery,
  useGetModelProvidersQuery,
} from "../../models/api/modelsApi";
import { routesMap } from "../../../router/routesMap";

const useWorkspaceData = () => {
  // Auth state from Redux
  const isAuthenticated = useAppSelector(authSlice.selectors.getIsAuthenticated);
  const isAuthInitialized = useAppSelector(authSlice.selectors.getIsInitialized);
  const navigate = useNavigate();

  // User data
  const {
    data: user,
    isLoading: isUserLoading,
    isFetching: isUserFetching,
    error: userError,
  } = useGetCurrentUserQuery(undefined, {
    skip: !isAuthenticated,
  });

  // Resources data
  const {
    data: resources,
    isLoading: isResourcesLoading,
    isFetching: isResourcesFetching,
    error: resourcesError,
  } = useGetResourcesQuery(undefined, {
    skip: !isAuthenticated,
  });

  const {
    data: models,
    isLoading: areModelsLoading,
    error: modelsError,
    isFetching: areModelsFetching,
  } = useGetModelsQuery();

  const {
    data: providers,
    isLoading: areProvidersLoading,
    error: providersError,
    isFetching: areProvidersFetching,
  } = useGetModelProvidersQuery();
  const {
    data: pricing,
    isLoading: arePricingLoading,
    error: pricingError,
    isFetching: arePricingFetching,
  } = useGetModelPricingsQuery();

  useEffect(() => {
    if ((userError as { status: number })?.status === 403) {
      navigate(routesMap.logout.url());
    }
  }, [userError, navigate]);

  // Combined loading state
  const isLoading = useMemo(() => {
    // Wait for auth to initialize first
    if (!isAuthInitialized) return true;

    if (areModelsLoading || areProvidersLoading || arePricingLoading) {
      return true;
    }

    // If authenticated, wait for all data to load
    if (isAuthenticated) {
      // Check if any queries are still loading
      if (isUserLoading || isResourcesLoading) {
        return true;
      }

      // All data is loaded and available
      return false;
    }

    // If auth is initialized and user is not authenticated, we're not in a loading state
    // This allows components to show login screens or redirects
    return false;
  }, [
    isAuthInitialized,
    isAuthenticated,
    isUserLoading,
    isResourcesLoading,
    areModelsLoading,
    areProvidersLoading,
    arePricingLoading,
  ]);

  // Combined fetching state
  const isFetching = useMemo(() => {
    // Wait for auth to initialize first
    if (!isAuthInitialized) return true;

    if (areModelsFetching || areProvidersFetching || arePricingFetching) {
      return true;
    }
    // If authenticated, wait for all data to load
    if (isAuthenticated) {
      // Check if any queries are still loading
      if (isUserFetching || isResourcesFetching) {
        return true;
      }
    }

    // If auth is initialized and user is not authenticated, we're not in a loading state
    // This allows components to show login screens or redirects
    return false;
  }, [
    isAuthInitialized,
    isAuthenticated,
    isUserFetching,
    isResourcesFetching,
    areModelsFetching,
    areProvidersFetching,
    arePricingFetching,
  ]);

  // Combined error state
  const error = userError || resourcesError || modelsError || providersError || pricingError;

  return {
    // Auth
    isAuthenticated,
    isAuthInitialized,

    // Data
    user,
    resources,

    // Models
    models,
    providers,
    pricing,

    // Status
    isLoading,
    isFetching,
    error,
  };
};

export { useWorkspaceData };
